import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checklist, InfoRequests } from '../components/workflow/Workflow';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { taxBasAPI, errText, visible } from '../lib/taxBasAPI';
import { ItrQuestion } from '../components/portal/ItrQuestion';
import { DocUploader, DocList } from '../components/portal/DocUploader';

const PAD = 'p-4 sm:p-6 lg:p-8';
const TONE = { 'Not Started': 'outline', 'In Progress': 'secondary', 'Information Required': 'destructive', Lodged: 'default' };

function YearList() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { taxBasAPI.itList().then(setData).catch((e) => toast.error(errText(e))); }, []);
  const open = async (e, y) => {
    try { nav(`/income-tax/${y.return_id || (await taxBasAPI.itStart(e.client_id, y.financial_year)).return_id}`); } catch (err) { toast.error(errText(err)); }
  };
  if (!data) return <p className={`${PAD} text-sm text-muted-foreground`}>Loading…</p>;
  return (
    <div className={`${PAD} space-y-6`} data-testid="income-tax-list">
      <h1 className="text-3xl font-semibold">Income Tax</h1>
      {data.entities.map((e) => (
        <Card key={e.client_id} data-testid={`it-entity-${e.client_id}`}>
          <CardHeader><CardTitle className="text-lg">{e.entity_name} <span className="text-xs font-normal text-muted-foreground">{e.entity_type}</span></CardTitle></CardHeader>
          <CardContent className="divide-y">
            {e.years.map((y) => (
              <div key={y.financial_year} className="flex items-center justify-between py-2">
                <span className="font-medium">FY {y.financial_year}</span>
                <span className="flex items-center gap-3">
                  {y.open_requests > 0 && <Badge variant="destructive">{y.open_requests} request(s)</Badge>}
                  <Badge variant={TONE[y.status_label] || 'secondary'} data-testid={`it-status-${e.client_id}-${y.financial_year}`}>{y.status_label}</Badge>
                  {(y.return_id || y.can_start) && <Button size="sm" variant={y.return_id ? 'outline' : 'default'} onClick={() => open(e, y)} data-testid={`it-open-${e.client_id}-${y.financial_year}`}>{y.return_id ? 'Open' : 'Start'}</Button>}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Requests({ ws, reload }) {
  const [txt, setTxt] = useState({});
  const open = ws.info_requests.filter((i) => i.status === 'open');
  if (!open.length) return null;
  return (
    <Card className="border-amber-300" data-testid="it-info-requests">
      <CardHeader><CardTitle className="text-base">Your accountant needs more information</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {open.map((i) => (
          <div key={i.id} className="space-y-1">
            <p className="text-sm">{i.text}</p>
            <Textarea value={txt[i.id] || ''} onChange={(e) => setTxt({ ...txt, [i.id]: e.target.value })} />
            <Button size="sm" onClick={async () => { try { await taxBasAPI.itRespond(ws.id, i.id, txt[i.id] || ''); reload(); } catch (e) { toast.error(errText(e)); } }}>Send reply</Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RentalProperty({ p, canUpload, upload, reload }) {
  const [key, setKey] = useState('');
  return (
    <div className="space-y-1 text-sm" data-testid={`it-rental-${p.property_id}`}>
      <Link className="text-primary underline" to={p.link} data-testid={`it-rental-link-${p.property_id}`}>{p.name || 'Rental property'}</Link>{p.address && p.address !== p.name && <span className="ml-1 text-xs text-muted-foreground" data-testid={`it-rental-address-${p.property_id}`}>{p.address}</span>} <span className="text-xs text-muted-foreground">{p.documents} document(s) this year</span>
      {p.checklist.length > 0 && (
        <p className="text-xs text-muted-foreground">Still needed: {p.checklist.filter((e) => !e.satisfied).map((e) => e.label).join(', ') || 'nothing – thanks'}</p>
      )}
      {canUpload && p.checklist.length > 0 && (
        <Select value={key || undefined} onValueChange={setKey}>
          <SelectTrigger className="h-8 max-w-sm" data-testid={`it-rental-kind-${p.property_id}`}><SelectValue placeholder="What is this document? (optional)" /></SelectTrigger>
          <SelectContent>{p.checklist.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <DocUploader disabled={!canUpload} testId={`it-rental-upload-${p.property_id}`}
        upload={(f, desc) => upload(f, { category: 'rental_property_documents', property_id: p.property_id, question_key: 'i_rental', checklist_key: key, description: desc })}
        onDone={() => { setKey(''); reload(); }} />
    </div>
  );
}

function RentalBox({ ws, canUpload, upload, reload }) {
  const [note, setNote] = useState('');
  const [changing, setChanging] = useState(false);
  const rc = ws.rental_confirmation;
  const confirm = async (confirmed) => {
    try { await taxBasAPI.itRentalConfirm(ws.id, confirmed, confirmed ? null : note); toast.success(confirmed ? 'Thanks – properties confirmed' : 'Sent to your accountant'); setChanging(false); setNote(''); reload(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="mt-2 space-y-2 rounded border p-3" data-testid="it-rental-properties">
      <p className="text-sm font-medium">Your rental properties</p>
      {ws.rental_properties.length === 0 && <p className="text-xs text-muted-foreground">No properties on file yet – upload your rental documents with the question above and we'll set the property up.</p>}
      {ws.rental_properties.map((p) => <RentalProperty key={p.property_id} p={p} canUpload={canUpload} upload={upload} reload={reload} />)}
      {ws.rental_properties.length > 0 && !ws.locked && (
        <div className="space-y-2 rounded bg-muted/40 p-2" data-testid="it-rental-confirm">
          {rc ? <p className="text-xs" data-testid="it-rental-confirm-status">{rc.confirmed ? 'You confirmed these properties' : 'You told us this list needs changes'} on {rc.at.slice(0, 10)}.</p>
            : <p className="text-xs">Are these all the rental properties you owned in FY {ws.financial_year}, with no sales or ownership changes?</p>}
          {changing && <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed? (sold, bought, ownership share…)" data-testid="it-rental-confirm-note" />}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => confirm(true)} data-testid="it-rental-confirm-yes">Yes, that's right</Button>
            {changing ? <Button size="sm" variant="outline" disabled={note.trim().length < 3} onClick={() => confirm(false)} data-testid="it-rental-confirm-send">Send changes</Button>
              : <Button size="sm" variant="outline" onClick={() => setChanging(true)} data-testid="it-rental-confirm-no">Something changed</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

function Workspace({ id }) {
  const [ws, setWs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saved, setSaved] = useState('');
  const [submitOpen, setSubmitOpen] = useState(false);
  const [agree, setAgree] = useState(false);
  const [missing, setMissing] = useState(false);
  const verRef = useRef(0);
  const timer = useRef(null);
  const pending = useRef({});
  const nav = useNavigate();
  const load = useCallback(() => taxBasAPI.itGet(id).then((w) => { setWs(w); setAnswers(w.answers); verRef.current = w.answers_version; })
    .catch((e) => { if ([403, 404].includes(e?.response?.status)) setMissing(true); else toast.error(errText(e)); }), [id]);
  useEffect(() => { load(); }, [load]);
  const persist = useCallback(async (a, opts = {}) => {
    clearTimeout(timer.current);
    const sent = { ...pending.current, ...a };
    pending.current = {};
    try {
      const r = await taxBasAPI.itSave(id, { answers: sent, confirmed: opts.confirmed || [], version: verRef.current, autosave: !!opts.autosave });
      verRef.current = r.answers_version; setSaved(new Date().toLocaleTimeString());
      if (opts.confirmed) load();
      return true;
    } catch (e) {
      pending.current = { ...sent, ...pending.current };
      toast.error(errText(e)); if (e?.response?.status === 409) load();
      return false;
    }
  }, [id, load]);
  useEffect(() => {
    const warn = (e) => { if (Object.keys(pending.current).length) { persist({}, { autosave: true }); e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => { window.removeEventListener('beforeunload', warn); if (Object.keys(pending.current).length) persist({}, { autosave: true }); };
  }, [persist]);
  const setAnswer = (k, v) => {
    setAnswers((cur) => ({ ...cur, [k]: v }));
    pending.current = { ...pending.current, [k]: v };
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist({}, { autosave: true }), 1200);
  };
  if (missing) return <div className={PAD}><p className="text-sm text-muted-foreground" data-testid="it-not-found">This income tax return isn't available. <Link to="/income-tax" className="text-primary">Back to Income Tax</Link></p></div>;
  if (!ws) return <p className={`${PAD} text-sm text-muted-foreground`}>Loading…</p>;
  const canUpload = !ws.locked || ws.status === 'information_required';
  const upload = (f, fields) => taxBasAPI.itUpload(id, f, fields);
  const submit = async () => {
    try { if (!(await persist(answers))) return; const w = await taxBasAPI.itGet(id); const r = await taxBasAPI.itSubmit(id, w.version); toast.success(r.already_submitted ? 'Already submitted' : 'Submitted – thank you'); setSubmitOpen(false); load(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(d?.unanswered ? `${d.message}: ${d.unanswered.map((x) => x.label).join('; ')}` : errText(e)); }
  };
  return (
    <div className={`${PAD} space-y-5`} data-testid="income-tax-workspace">
      <Link to="/income-tax" className="text-sm text-primary">← All years</Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{ws.entity_name} · FY {ws.financial_year}</h1>
          <p className="text-xs text-muted-foreground">Questionnaire v{ws.questionnaire_version} {saved && `· saved ${saved}`}</p>
        </div>
        <Badge data-testid="it-ws-status">{ws.status_label}</Badge>
      </div>
      {ws.locked && <p className="rounded bg-muted p-3 text-sm" data-testid="it-locked-note">Submitted {ws.submitted_at?.slice(0, 10)} – locked. Submission is not lodgment; we'll prepare your return and ask you to sign before lodging.</p>}
      {ws.reopen && <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="it-reopen-note"><b>Reopened by your accountant{ws.reopen.at ? ` on ${ws.reopen.at.slice(0, 10)}` : ''}:</b> {ws.reopen.reason}</p>}
      <Requests ws={ws} reload={load} />
      {ws.sections.map((s) => (
        <Card key={s.title} data-testid={`it-section-${s.title}`}>
          <CardHeader><CardTitle className="text-lg">{s.title}</CardTitle></CardHeader>
          <CardContent>
            {s.questions.filter((q) => visible(q, answers)).map((q) => (
              <ItrQuestion key={q.key} q={q} ws={ws} answers={answers} setAnswer={setAnswer} upload={upload} reload={load} canUpload={canUpload}
                confirm={(k) => persist({}, { confirmed: [k] })} />
            ))}
            {s.title === 'Income' && (answers.i_rental === 'yes' || ws.rental_properties.length > 0) && <RentalBox ws={ws} canUpload={canUpload} upload={upload} reload={load} />}
          </CardContent>
        </Card>
      ))}
      <Card><CardHeader><CardTitle className="text-base">Other documents</CardTitle></CardHeader><CardContent>
        <DocList docs={ws.documents.filter((d) => d.itr_category === 'other')} />
        <DocUploader disabled={!canUpload} testId="it-upload-other" upload={(f, desc) => upload(f, { category: 'other', description: desc })} onDone={load} />
      </CardContent></Card>
      <Checklist kind="income_tax" refId={ws.id} cl={ws.checklist} reload={load} locked={!canUpload} onUpload={(f, i, desc) => upload(f, { category: i.source === 'questionnaire' ? i.key : 'other', question_key: i.question_key, checklist_key: i.source === 'questionnaire' ? null : i.key, description: desc })} />
      <InfoRequests kind="income_tax" refId={ws.id} items={ws.info_threads} reload={load} />
      {!ws.locked && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => persist(answers).then((ok) => ok && toast.success('Draft saved'))} data-testid="it-save-draft">Save draft</Button>
          <Button variant="ghost" onClick={() => persist(answers).then((ok) => ok && nav('/income-tax'))} data-testid="it-continue-later">Continue later</Button>
          <Button onClick={() => setSubmitOpen(true)} data-testid="it-submit-open">Submit</Button>
        </div>
      )}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent data-testid="it-submit-dialog">
          <DialogHeader><DialogTitle>Submit FY {ws.financial_year}</DialogTitle></DialogHeader>
          <label className="flex items-start gap-2 text-sm"><Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="it-declaration" />{ws.declaration_text}</label>
          <p className="text-xs text-muted-foreground">Submitting sends this to your accountant. It is not lodgment with the ATO.</p>
          <DialogFooter><Button disabled={!agree} onClick={submit} data-testid="it-submit-confirm">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function IncomeTaxPortal() {
  const { id } = useParams();
  return id ? <Workspace id={id} /> : <YearList />;
}
